import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Repeat2 } from "lucide-react";

/**
 * ReshareModal — share someone else's public post to your own profile with
 * an optional 500-char note. Credit stays with the ORIGINAL author (backend
 * flattens chain reshares to root).
 */
const MAX = 500;

export default function ReshareModal({ post, onClose, onReshared }) {
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Hide the global Add FAB while the modal is open.
  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, []);

  async function send() {
    if (sending) return;
    setSending(true);
    try {
      const r = await api.post(`/commons/posts/${post.id}/reshare`, {
        note: note.trim().slice(0, MAX),
      });
      toast.success("Reshared to your profile");
      onReshared?.(r.data);
    } catch (e) {
      // 409 = already reshared (dedupe). Still a "no new work" outcome — treat
      // like a soft-success so the UI reflects the real state and the user
      // isn't left thinking the button is broken.
      if (e?.response?.status === 409) {
        toast("You already reshared this post.");
        onReshared?.({ id: e?.response?.data?.detail?.reshare_id });
        return;
      }
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not reshare — please try again";
      toast.error(msg, {
        action: {
          label: "Retry",
          onClick: () => send(),
        },
      });
    } finally {
      setSending(false);
    }
  }

  const remaining = MAX - note.length;
  const over = remaining < 0;
  const originalAuthor = post.author?.name || "someone";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-[rgba(0,0,0,0.7)] backdrop-blur-sm"
      onClick={onClose}
      data-testid="reshare-modal-backdrop"
    >
      <div
        className="w-full max-w-md placed-card p-6 relative"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="reshare-modal"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="reshare-close"
          className="absolute top-3 right-3 text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors"
          aria-label="close"
        >
          <X size={16} strokeWidth={1.5} />
        </button>

        <div className="flex items-center gap-2">
          <Repeat2 size={14} strokeWidth={1.6} className="text-[var(--brass)]" />
          <p className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--brass)] opacity-80">
            reshare to your profile
          </p>
        </div>
        <h2 className="mt-2 font-serif-lit text-2xl text-[var(--cream)]">
          share @{originalAuthor}&apos;s post
        </h2>
        <p className="mt-2 font-garamond italic text-sm text-[var(--cream-dim)]">
          credit stays with the original author. your note (optional) appears above.
        </p>

        <label className="block mt-5">
          <span className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-dim)]">
            your note — optional
          </span>
          <textarea
            ref={inputRef}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="why share this?"
            rows={3}
            data-testid="reshare-note"
            className="mt-2 w-full bg-[var(--bg)] border border-[rgba(179,137,90,0.24)] rounded p-3 font-garamond italic text-[var(--cream)] placeholder:text-[var(--cream-mute)] focus:outline-none focus:border-[var(--brass)] resize-none"
          />
        </label>
        <p
          className={`mt-1 font-mono-lc text-[10px] tracking-[0.14em] text-right ${over ? "text-[#C86A5C]" : "text-[var(--cream-mute)]"}`}
          data-testid="reshare-counter"
        >
          {remaining} left
        </p>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} data-testid="reshare-cancel" className="lc-btn-ghost">
            never mind
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || over}
            data-testid="reshare-send"
            className="lc-btn"
          >
            {sending ? "sharing…" : "reshare"}
          </button>
        </div>
      </div>
    </div>
  );
}
